import {Inject, Injectable, OnDestroy, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {AuthenticationService} from './authentication.service';
import {CookieService} from '../_services/cookie.service';
import {CookieType} from '../x-models/x-enums/cookie-type';
import {UserJwt} from '../models/admin_owner/user/user-jwt';
import {GateRefreshResult} from './contact/contact-link.models';
import {isLockoutPath, lockoutRouteForSession} from './gate-lockout-route';
import {GateHubClient} from './gate-hub-client';

/**
 * Minimum spacing between two `/auth/gates/refresh` calls raised by the **daily watchdog** or the
 * hub-down visibility backstop.
 *
 * It no longer throttles the triggers that matter. Since the gates hub was added (#558), everything that
 * carries information — a server push, a (re)connect, a deadline, app start — bypasses this entirely, because
 * each of them means "something you do not know about may just have happened" and delaying that by five
 * minutes is precisely the bug the push was added to fix. What remains throttled is the pair of triggers that
 * carry no information at all and only exist for the case where the socket never came up.
 */
export const GATE_REFRESH_THROTTLE_MS = 5 * 60 * 1000;

/**
 * How stale a successful check may get before the daily watchdog forces one.
 *
 * Checking gates only at login means an employee who never signs out can go a month without the requirement
 * assigned to them ever being noticed — the hole #558's live refresh exists to close. The push closes it in
 * seconds when the socket is up; this is the outer bound when it is not.
 */
export const GATE_REFRESH_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * How often the watchdog timer looks at the clock while the tab stays open.
 *
 * This is NOT a poll interval — a tick only leads to a request when the last successful check is already
 * older than {@link GATE_REFRESH_MAX_AGE_MS}. So a tab left open for days is refreshed roughly daily even
 * with no socket and no gestures, and detected within five minutes of becoming due rather than at some
 * arbitrary offset.
 */
export const GATE_REFRESH_TIMER_TICK_MS = 5 * 60 * 1000;

/**
 * Minimum spacing between two checks raised by the visibility backstop.
 *
 * Measured against the last ATTEMPT of any trigger, so coming back to a tab seconds after app start does not
 * fire a second request.
 */
export const GATE_REFRESH_VISIBILITY_THROTTLE_MS = 60 * 1000;

/**
 * How long after a deadline the alarm fires.
 *
 * A deadline is decided server-side against the server's clock; the browser's may be a little ahead. Firing
 * exactly on the local instant would ask a server that still considers the session inside its window, get
 * `changed:false`, and then never ask again — the alarm is one-shot per deadline. A few seconds late is
 * invisible and always right.
 */
export const GATE_REFRESH_DEADLINE_SKEW_MS = 5_000;

/**
 * How long **before** a token's expiry the alarm fires.
 *
 * The other direction from {@link GATE_REFRESH_DEADLINE_SKEW_MS}, and for the opposite reason: a `review`
 * verdict time-boxes the token, so its expiry is a gate deadline too — but a refresh sent with an already
 * expired token is a 401, which is `ErrorInterceptor`'s business (a logout), not a gate refresh. Asking a
 * little early re-mints while the session is still alive.
 */
export const GATE_REFRESH_EXPIRY_LEAD_MS = 30_000;

/** `setTimeout`'s ceiling (2^31-1 ms ≈ 24.8 days). A longer delay silently fires *immediately* instead. */
export const GATE_REFRESH_MAX_TIMEOUT_MS = 2_147_483_647;

/** The localStorage key holding the last SUCCESSFUL check (ISO-8601). */
export const GATE_REFRESH_LAST_CHECK_KEY = 'gate_refresh_last_check';

/**
 * What raised a refresh.
 *
 * | trigger      | throttled?                                   | raised by |
 * | ------------ | -------------------------------------------- | --------- |
 * | `start`      | no                                           | the constructor — app open or reload |
 * | `push`       | no                                           | `GateVerdictMayHaveChanged` on the gates hub |
 * | `reconnect`  | no                                           | the hub connecting, first time or after a drop |
 * | `deadline`   | no                                           | the one-shot alarm on the session's earliest deadline |
 * | `daily`      | 5 min between attempts **and** only when the last success is older than 24 h | the watchdog interval |
 * | `visibility` | 60 s since the last attempt, **and** only while the hub is down | the tab becoming visible |
 */
export type GateRefreshTrigger = 'start' | 'push' | 'reconnect' | 'deadline' | 'daily' | 'visibility';

/**
 * Keeps the session's GATE CLAIMS live while the SPA is open (#558).
 *
 * ### Why it exists
 *
 * All three gates — account security (#282), required forms (#404), required contacts (#558) — are decided
 * when the token is minted and then ride along in it. That is correct and cheap, and it has one hole: nothing
 * re-asks. An admin assigning a requirement to somebody whose tab has been open since Monday changes nothing
 * for them until they next sign in, which for a shared till account can be never. This service closes that
 * hole from the client side: `POST /auth/gates/refresh` re-evaluates every gate the session carries, answers
 * `{changed:false}` in the overwhelmingly common case, and hands back a re-minted token only when a verdict
 * actually moved.
 *
 * ### Push first, clock second, safety net last
 *
 * The first version of this was a poll on `focus`/`visibilitychange` throttled to five minutes, and the first
 * manual smoke found its flaw immediately: the manager assigned a rule, the employee's tab was already
 * focused, and nothing happened for five minutes because the throttle window had been seeded by the check
 * that ran at login. So the design is now, in order of who knows something:
 *
 * 1. **The server pushes** ({@link GateHubClient}). It is the only party that knows the instant a
 *    requirement, a link or a policy changes. A push refreshes immediately, throttle or no throttle.
 * 2. **The clock fires** for the deadlines the *session itself* carries — a forms or contact review deadline,
 *    and the token's own expiry. Nothing pushes when a grace window simply runs out; only time passes.
 * 3. **The safety net** catches everything else: app start/reload, every (re)connect (a push raised while the
 *    socket was down is gone — SignalR does not replay), a daily watchdog, and — only while the hub is NOT
 *    connected — the tab becoming visible. `focus` is gone entirely: it fired constantly, told us nothing,
 *    and its throttle was the actual bug.
 *
 * ### What it must never do
 *
 * **Never sign anybody out, and never show an error.** A background check nobody asked for has no right to
 * interrupt work: every failure here is logged and swallowed (see {@link fail}). The gates themselves are
 * still enforced server-side on every request, so a refresh that never succeeds costs correctness nothing —
 * it only delays a *notification*. That is also why a 401 raised by this call is left entirely to
 * `ErrorInterceptor`, which already owns the recover-or-logout decision for a dead session.
 *
 * ### What it deliberately skips
 *
 * * **No session** — nothing to refresh, and the endpoint would 401.
 * * **Already on a lockout screen** — `two-factor-required`, `forms-required` and `contact-required` each poll
 *   their own gate's status, which is fresher and narrower. The employment waiting screen (#864) is not one of
 *   them: it has nothing of its own to poll, so this service is what lifts it. Swapping the token under a half-finished wizard
 *   buys nothing and can lose a remediation in progress.
 *
 * ### Lifetime
 *
 * `providedIn: 'root'`, started by `AppComponent`'s constructor injecting it — the same arrangement, and for
 * the same reason, as `ContextExpiryTimerService`: Angular DI is lazy, so a service nothing injects never
 * runs. It is also the **single owner** of {@link GateHubClient}: it starts the hub and stops it on teardown,
 * so there is never a question of which of the two closes the socket.
 */
@Injectable({providedIn: 'root'})
export class GateRefreshService implements OnDestroy {
  /**
   * When the last **successful** check happened, or null when there has never been one in this browser.
   *
   * Exposed for debugging (and for a spec to read without reaching into `localStorage`): this service is
   * otherwise entirely invisible when it works, which makes "did it even run?" the first question anybody has.
   */
  readonly lastCheckedAt = signal<Date | null>(null);

  /** True while a `/auth/gates/refresh` is in flight — the guard against overlapping calls. */
  readonly inFlight = signal(false);

  /**
   * When a request was last ATTEMPTED (successful or not), driving the two throttled triggers.
   *
   * Attempts rather than successes, deliberately: a server answering 500 to every call must not be asked
   * again on every gesture. The 24 h force reads the persisted *success* instead, so a long outage still
   * leaves the session refreshing as soon as it can.
   */
  private lastAttemptMs: number | null = null;

  /** The watchdog handle, cleared on destroy. */
  private timer: ReturnType<typeof setInterval> | null = null;

  /** The one-shot deadline alarm, re-armed on every session swap and cleared on destroy. */
  private deadlineTimer: ReturnType<typeof setTimeout> | null = null;

  /** Subscriptions to the hub's signals and to the session, both dropped on destroy. */
  private hubSubscription: Subscription | null = null;
  private sessionSubscription: Subscription | null = null;

  constructor(private http: HttpClient,
              @Inject('BASE_URL') private baseUrl: string,
              private auth: AuthenticationService,
              private router: Router,
              private cookieService: CookieService,
              private hub: GateHubClient) {
    this.lastCheckedAt.set(readPersistedCheck());

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.timer = setInterval(() => this.dailyTick(), GATE_REFRESH_TIMER_TICK_MS);

    // `auth.user` is a BehaviorSubject, so this arms the alarm for the session the SPA booted with and
    // re-arms it for every later one (login, logout, a context enter, a token swapped by a reissue).
    this.sessionSubscription = this.auth.user.subscribe(() => this.armDeadlineAlarm());

    this.hubSubscription = this.hub.signals.subscribe(sig => {
      if (sig.kind === 'connected') {
        // A push raised while the socket was down was never delivered — catch up once, every time.
        this.refreshNow('reconnect');
        return;
      }
      // The reason is logged, never branched on: an unknown one must refresh exactly like a known one.
      console.debug(`[gate-refresh] push: ${sig.reason}`);
      this.refreshNow('push');
    });
    this.hub.start();

    // App open or reload. Unthrottled, deliberately: a reload is a person asking for the current state, and
    // this is also the ONLY trigger a browser with no working socket has at page level.
    this.refreshNow('start');
  }

  /**
   * Tears every global hook down again. In the running app a root service lives as long as the page, so this
   * never fires; it exists for the TEST RUN, where every TestBed builds and destroys its own root injector.
   * Without it each spec's instance kept its `visibilitychange` listener, its interval, its deadline alarm and
   * its hub connection alive, and a later, unrelated spec that dispatched an event drove an HTTP call through
   * an already-destroyed injector (`NG0205`), failing tests that had nothing to do with this service.
   */
  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.clearDeadlineAlarm();
    this.hubSubscription?.unsubscribe();
    this.hubSubscription = null;
    this.sessionSubscription?.unsubscribe();
    this.sessionSubscription = null;
    this.hub.stop();
  }

  /**
   * Refresh **now**, for a trigger that carries information — a push, a (re)connect, a deadline, app start.
   *
   * Bypasses every throttle by design. It still refuses in the three cases where a request would be wrong
   * rather than merely redundant:
   *
   * 1. **In flight** — a burst (a manager saving three requirements in a row) must produce one request, not
   *    three. The one in flight already answers all of them: it is evaluated server-side after they landed.
   * 2. **No session** — nothing to ask about, and the endpoint would 401.
   * 3. **On a lockout screen** — see the class remarks.
   */
  refreshNow(trigger: GateRefreshTrigger): void {
    if (!this.eligible()) return;
    if (this.inFlight()) return;

    this.lastAttemptMs = Date.now();
    this.run(trigger);
  }

  /** Whether a request is admissible at all, independent of any throttle. */
  private eligible(): boolean {
    if (!this.auth.userValue?.jwt) return false;
    return !isLockoutPath(this.currentPath());
  }

  /**
   * The watchdog tick: act only when this session has genuinely gone stale.
   *
   * With the push in place this fires essentially never — which is the point. It is what makes the invariant
   * hold for a tab whose socket never came up at all, and it costs one clock comparison every five minutes.
   */
  private dailyTick(): void {
    if (!this.eligible() || this.inFlight()) return;

    const now = Date.now();
    if (this.lastAttemptMs !== null && (now - this.lastAttemptMs) < GATE_REFRESH_THROTTLE_MS) return;

    const lastCheck = this.lastCheckedAt()?.getTime() ?? null;
    // A session that has never had a successful check is stale by definition — that is the retry path after a
    // failed `start`, and the only reason the watchdog runs at all inside the first 24 h.
    if (lastCheck !== null && (now - lastCheck) < GATE_REFRESH_MAX_AGE_MS) return;

    this.lastAttemptMs = now;
    this.run('daily');
  }

  /**
   * The **degraded-mode** backstop: the tab became visible and there is no push channel to rely on.
   *
   * Kept — against the instinct to delete both gesture triggers with the push — for one concrete case that is
   * not hypothetical: a server without `/hubs/gates` (a partial deploy, a proxy that drops WebSockets, a
   * network that blocks them), where the ONLY remaining triggers would be app start and a 24-hour watchdog.
   * Gated on `hub.connected()` so it is completely inert whenever the push is working, and throttled to a
   * minute so it can never become the five-minute poll it replaces.
   */
  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (this.hub.connected()) return;
    if (!this.eligible() || this.inFlight()) return;

    const now = Date.now();
    if (this.lastAttemptMs !== null && (now - this.lastAttemptMs) < GATE_REFRESH_VISIBILITY_THROTTLE_MS) return;

    this.lastAttemptMs = now;
    this.run('visibility');
  };

  // ----- the deadline alarm ------------------------------------------------------------------------------

  /**
   * (Re)arm the one-shot alarm on this session's earliest deadline.
   *
   * Nothing pushes when a grace window simply runs out — only time passes — so the clock is the only thing
   * that can notice. One timer, not one per deadline: the earliest is the only one that matters, and firing
   * it swaps the token, which re-arms this from the new claims.
   */
  private armDeadlineAlarm(): void {
    this.clearDeadlineAlarm();
    const target = this.nextDeadline();
    if (target !== null) this.scheduleDeadline(target);
  }

  /**
   * The earliest instant at which this session's verdict could change on its own, or null when there is none.
   *
   * Three sources, and each is skewed in the direction that makes it useful:
   *
   * * `forms_review_due` and `contact_review_due` (the grace deadlines) and `employment_starts` (#864). Asked
   *   a few seconds **after**, so the server (whose clock decides) agrees the instant has passed. See
   *   {@link GATE_REFRESH_DEADLINE_SKEW_MS}.
   * * the token's own `expiration` — a `review` verdict time-boxes the token, so its expiry is a gate
   *   deadline too. Asked shortly **before**, because a refresh sent with a dead token is a 401 and a logout.
   *
   * Only instants still in the future are considered. A deadline already in the past needs no timer: whatever
   * arms this (app start, a session swap) is itself a refresh trigger, so it has just been asked about — and
   * scheduling a zero-delay timer whose handler re-arms from the same unchanged session is an infinite loop.
   */
  private nextDeadline(): number | null {
    const user = this.auth.userValue;
    if (!user?.jwt) return null;

    const now = Date.now();
    const candidates: number[] = [];
    const consider = (at: Date | null | undefined, offsetMs: number): void => {
      if (!at) return;
      const instant = at.getTime() + offsetMs;
      if (Number.isFinite(instant) && instant > now) candidates.push(instant);
    };

    consider(this.auth.formsReviewDue, GATE_REFRESH_DEADLINE_SKEW_MS);
    consider(this.auth.contactReviewDue, GATE_REFRESH_DEADLINE_SKEW_MS);
    // The employment start (#864): nothing pushes when a start date arrives, only time passes. Asked a few
    // seconds after it, like the review deadlines. The server keeps such a token alive two minutes past the start
    // (EmploymentGatePolicy.StartOverlap) so that this call still has a live token to ask with.
    consider(this.auth.employmentStartsAt, GATE_REFRESH_DEADLINE_SKEW_MS);
    consider(user.expiration ? new Date(user.expiration) : null, -GATE_REFRESH_EXPIRY_LEAD_MS);

    return candidates.length ? Math.min(...candidates) : null;
  }

  /**
   * Schedule the alarm, chaining through {@link GATE_REFRESH_MAX_TIMEOUT_MS} for delays a `setTimeout` cannot express.
   *
   * A `setTimeout` asked to wait longer than 2^31-1 ms fires **immediately** instead — silently, and the
   * failure mode is not a missed alarm but a request loop. A grace window of a year is a perfectly ordinary
   * `RequirementGraceDays` value, so this is a real case, not a theoretical one.
   */
  private scheduleDeadline(target: number): void {
    const remaining = target - Date.now();
    const delay = deadlineChunkMs(remaining);

    this.deadlineTimer = setTimeout(() => {
      this.deadlineTimer = null;
      // A chunked wait is not the deadline — go round again for what is left of it.
      if (delay < remaining) {
        this.scheduleDeadline(target);
        return;
      }
      this.refreshNow('deadline');
    }, delay);
  }

  /** Cancel a pending alarm. Safe when there is none. */
  private clearDeadlineAlarm(): void {
    if (this.deadlineTimer !== null) clearTimeout(this.deadlineTimer);
    this.deadlineTimer = null;
  }

  // ----- the round-trip ----------------------------------------------------------------------------------

  /** The actual round-trip. Separated so the trigger methods read as the policy they are. */
  private run(trigger: GateRefreshTrigger): void {
    this.inFlight.set(true);

    this.http.post<GateRefreshResult<Partial<UserJwt>>>(`${this.baseUrl}/auth/gates/refresh`, {},
      {withCredentials: true}).subscribe({
      next: result => {
        this.inFlight.set(false);
        this.recordCheck(result?.checkedUtc);

        if (!result?.changed || !result.token) return;

        // The ONE token-swap path, shared with every reissue screen — never a second merge implementation.
        this.auth.applyReissuedSession(result.token);
        // Belt and braces: `applyReissuedSession` publishes on `auth.user`, which re-arms the alarm through
        // the subscription above. Doing it here too keeps the alarm correct even for a caller that swapped a
        // token without going through the subject, and `armDeadlineAlarm` clears before it schedules.
        this.armDeadlineAlarm();
        this.routeAfterChange();
      },
      error: err => {
        this.inFlight.set(false);
        this.fail(trigger, err);
      }
    });
  }

  /**
   * A verdict moved. Send the session where the (now updated) claims say it belongs — and nowhere else.
   *
   * `restricted` ⇒ the matching lockout screen, chosen by the SAME rule the guard and the error interceptor
   * read, so a background refresh can never disagree with the navigation that follows it.
   *
   * `review` ⇒ nothing to navigate. The panel's warning strip reads the session claims, and
   * `applyReissuedSession` pushes the merged session onto `AuthenticationService.user`, so a component bound
   * to that observable re-renders on its own.
   *
   * `ok` ⇒ nothing at all, which is the whole point: a restriction that was lifted elsewhere must not yank
   * somebody out of what they are doing.
   */
  private routeAfterChange(): void {
    const lockout = lockoutRouteForSession(this.auth);
    if (lockout) this.router.navigate(['/' + lockout]).then();
  }

  /** Persist and publish a successful check. The server's own instant is preferred over the local clock. */
  private recordCheck(checkedUtc?: string): void {
    const parsed = checkedUtc ? new Date(checkedUtc) : null;
    const checkedAt = parsed && Number.isFinite(parsed.getTime()) ? parsed : new Date();
    this.lastCheckedAt.set(checkedAt);

    // NB: CookieService traps the global localStorage.setItem (it THROWS) — every persistent write must go
    // through it. Required category: this is not a preference, it is how a session-security check knows how
    // stale it is, and the 24 h watchdog stops working if the write is declined.
    try {
      this.cookieService.setLocalStorage(CookieType.Required, GATE_REFRESH_LAST_CHECK_KEY,
        checkedAt.toISOString());
    } catch {
      // Storage refused (private mode, quota). The in-memory value still drives this tab; only the "how stale
      // across reloads" half is lost, and losing it makes the service check MORE often, never less.
    }
  }

  /**
   * A failed refresh, in full: log it and carry on.
   *
   * Not an alert, not a logout, not a retry loop. See the class remarks — the gates are still enforced
   * server-side on every request, so the only thing a failure here delays is a notification.
   */
  private fail(trigger: GateRefreshTrigger, err: unknown): void {
    console.warn(`[gate-refresh] ${trigger} check failed`, err);
  }

  /**
   * The current path.
   *
   * Read from `location` rather than `Router.url` because the very first check runs during app bootstrap,
   * before the first navigation has settled — exactly the moment a session restored from `localStorage` onto
   * a lockout screen has to be recognised as such.
   */
  private currentPath(): string {
    return location.pathname + location.search;
  }
}

/**
 * How long a single `setTimeout` may wait for a deadline: the whole remaining delay, or one full chunk when
 * that is longer than a timeout can express.
 *
 * Pure and exported so the cap can be pinned by a spec directly. It has to be: `jasmine.clock()` schedules a
 * 30-day delay perfectly happily, so a behavioural test could never reproduce the *browser's* failure — a
 * `setTimeout` asked to wait longer than 2^31-1 ms fires immediately instead, turning a distant deadline into
 * a request loop. The rule is what needs pinning, not the platform.
 *
 * Negative delays clamp to 0 rather than being rejected: a deadline that slipped into the past between the
 * computation and the schedule should fire at once, not never.
 */
export function deadlineChunkMs(remainingMs: number): number {
  if (remainingMs > GATE_REFRESH_MAX_TIMEOUT_MS) return GATE_REFRESH_MAX_TIMEOUT_MS;
  return Math.max(remainingMs, 0);
}

/** The persisted last-successful-check instant, or null when absent/unparseable. `getItem` is not trapped. */
function readPersistedCheck(): Date | null {
  const raw = localStorage.getItem(GATE_REFRESH_LAST_CHECK_KEY);
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}
